import React, { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// MUI
import Toolbar from '@mui/material/Toolbar';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';

// Custom Icons
import EnaSettingIcon from '@/icons/EnaSettingIcon';

// Custom components
import FormContent, { FormContentMode } from '@/components/FormContent';
import { GridContainer, GridItem } from '@/ui/Grid';
import {
  CardActionButtonSave,
  CardHeading,
  CardToolbarButtonBack,
  CardToolbarMenuButton,
  CardToolbarMenuItemDelete,
} from '@/ui/Card';
import {
  FormAvatar,
  FormHeading,
  FormHeadingID,
  FormFillContent,
} from '@/ui/Form';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';
import dataController from '@/lib/DataController';
import useFormController from '@/components/useFormController';

interface IEnaSettingFormProps {
  dc: dataController;
  recordId: number;
  mode: FormContentMode;
  form: string;
}

const EnaSettingForm: FC<IEnaSettingFormProps> = (props) => {
  const navigate = useNavigate();

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;

  const { dc, recordId, mode, form } = props;
  const {
    record,
    validation,
    fields,
    onFieldChange,
    doUpdate,
    doDelete,
    doClose,
    pickerDataControllers,
    dataChanged,
    doRefresh
  } = useFormController({
    mode,
    recordId,
    form,
    dc,
    onClose: () => {},
  });

  const handleClickBack = () => {
    if (doClose) {
      doClose()
        .then((resp) => {
          if (resp.success) {
            navigate('/admin/ena_settings');
          } 
        })
        .catch((result) => {
          snackbarContext.showNotification(result.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_ena_setting_success';
            snackbarContext.showNotification(msg, 'success');
          }
        })
        .catch((result) => {
          if (result.success === false && result.validationPass === false) {
            // do nothing
          } else {
            const msg = 'error.update_ena_setting';
            snackbarContext.showNotification(msg, 'error');
          }
        })
        .finally(() => {
          doRefresh();
        });
    }
  };

  const handleDelete = () => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.delete_ena_setting_success';
            snackbarContext.showNotification(msg, 'success');
            navigate('/admin/ena_settings');
          }
        })
        .catch(() => {
          const msg = 'error.delete_ena_setting';
          snackbarContext.showNotification(msg, 'error');
        });
    }
  };

  const title = record.tenant_id ? (record.tenant_id as string) : undefined;

  return (
    <Card>
      <CardHeading>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar ariaLabel="model" icon={<EnaSettingIcon />} />
          <FormHeading
            header={title}
            subheaderContent={<FormHeadingID id={recordId} />}
            dataChanged={dataChanged}
          />
          <FormFillContent />
          <CardToolbarButtonBack onClick={handleClickBack} />
          <CardToolbarMenuButton>
            <CardToolbarMenuItemDelete onClick={handleDelete} />
          </CardToolbarMenuButton>
        </Toolbar>
      </CardHeading>
      <CardContent>
        <GridContainer direction="row" spacing={3}>
          <GridItem>
            <GridContainer direction="row">
              <FormContent
                fieldNames={[
                  'tenant_id',
                  'koef_korisna_pov',
                  'zapisnici_folder',
                  'geodata_izvjestaj_enabled',
                  'geodata_izvjestaj_email',
                  'hide_zapisnik_if_no_filename',
                ]}
                record={record}
                validation={validation}
                onFieldChange={onFieldChange}
                fields={fields}
                mode={mode}
                pickerDataControllers={pickerDataControllers}
                columns={1}
              />
            </GridContainer>
          </GridItem>
        </GridContainer>
      </CardContent>
      <CardActions>
        <FormFillContent />
        <CardActionButtonSave
          onClick={handleUpdate}
          variant="contained"
          disabled={!dataChanged}
        />
      </CardActions>
    </Card>
  );
}

export default EnaSettingForm;
